<script lang="ts">
	import DefaultButton from '../../../../basic/buttons/DefaultButton.svelte';
	import IconButton from '../../../../basic/buttons/IconButton.svelte';
	import DefaultTextInput from '../../../../basic/input/DefaultTextInput.svelte';
	import InputLabel from '../../../../basic/input/InputLabel.svelte';
	import Modal from '../../../../basic/modal/Modal.svelte';
	import ModalHeader from '../../../../basic/modal/ModalHeader.svelte';
	import { handleStoreError } from '../../../../stores/GeneralStore';
	import { createTextBlockStore } from '../../../../stores/TextBlockStore.svelte';
	import type {
		PatchSettingDocumentType,
		SettingDocumentNameGenerator,
		SettingDocumentType,
		SettingPaymentPeriod
	} from '../../../../types/BasicTypes';
	import type { TextBlock } from '../../../../types/TextBlocksTypes';
	import TextBlocks from '../../../textblocks/TextBlocks.svelte';
	import { _ } from 'svelte-i18n';
	import SettingDocumentNameGenerators from '../settingdocumentnamegenerators/SettingDocumentNameGenerators.svelte';
	import SettingPaymentPeriods from '../settingpaymentperiods/SettingPaymentPeriods.svelte';

	interface Props {
		targetDocumentType: SettingDocumentType;
		patchSettingDocumentType: (id: number, request: PatchSettingDocumentType) => Promise<void>;
		onModalClose: () => void;
	}
	let { targetDocumentType, patchSettingDocumentType, onModalClose }: Props = $props();

	let defaultName: string = $derived(targetDocumentType.defaultName);
	let showNameGeneratorSelection: boolean = $state(false);
	let showOpeningTextSelection: boolean = $state(false);
	let showClosingTextSelection: boolean = $state(false);
	let showPaymentPeriodSelection: boolean = $state(false);

	let nameGeneratorObject: SettingDocumentNameGenerator | undefined = $derived(
		targetDocumentType.settingDocumentNameGenerator
	);
	let openingTextObject: TextBlock | undefined = $derived(targetDocumentType.openingText);
	let closingTextObject: TextBlock | undefined = $derived(targetDocumentType.closingText);
	let paymentPeriodObject: SettingPaymentPeriod | undefined = $derived(
		targetDocumentType.settingPaymentPeriod
	);

	let textBlockStore = createTextBlockStore();

	async function handlePatch() {
		//Build the request
		const request: PatchSettingDocumentType = {
			defaultName: defaultName,
			fkDocumentNameGenerator: { value: nameGeneratorObject?.id },
			fkDefaultOpeningText: { value: openingTextObject?.id },
			fkDefaultClosingText: { value: closingTextObject?.id },
			fkDefaultPaymentPeriod: { value: paymentPeriodObject?.id }
		};

		try {
			await patchSettingDocumentType(targetDocumentType.id, request);
			onModalClose();
		} catch (error) {
			handleStoreError('general.patch_failed', error);
		}
	}
</script>

<Modal {onModalClose} onModalEnter={handlePatch}>
	{#snippet header()}
		<ModalHeader text={$_('settings.basic.document_labels.lbl_patch_header')} />
	{/snippet}
	{#snippet content()}
		<div style="min-height: 360px;" class="grid gap-2 content-start">
			<!--Name-->
			<InputLabel label={$_('settings.basic.document_labels.lbl_name')} />
			<DefaultTextInput propertyName="defaultName" bind:value={defaultName} />

			<!--Name generator-->
			<InputLabel label={$_('settings.basic.document_labels.lbl_name_generator')} />
			<div class="flex gap-2">
				<div class="grow">
					<DefaultTextInput
						propertyName=""
						disabled={true}
						value={nameGeneratorObject?.defaultName || ''}
					/>
				</div>
				<IconButton iconName="search" onClick={() => (showNameGeneratorSelection = true)} />
				<IconButton
					disabled={!nameGeneratorObject}
					iconName="eraser"
					onClick={() => (nameGeneratorObject = undefined)}
				/>
			</div>

			<!--Opening text-->
			<InputLabel label={$_('settings.basic.document_labels.lbl_default_opening_text')} />
			<div class="flex gap-2">
				<div class="grow">
					<DefaultTextInput
						propertyName=""
						disabled={true}
						value={openingTextObject?.shortName || ''}
					/>
				</div>
				<IconButton iconName="search" onClick={() => (showOpeningTextSelection = true)} />
				<IconButton
					disabled={!openingTextObject}
					iconName="eraser"
					onClick={() => (openingTextObject = undefined)}
				/>
			</div>

			<!--Closing text-->
			<InputLabel label={$_('settings.basic.document_labels.lbl_default_closing_text')} />
			<div class="flex gap-2">
				<div class="grow">
					<DefaultTextInput
						propertyName=""
						disabled={true}
						value={closingTextObject?.shortName || ''}
					/>
				</div>
				<IconButton iconName="search" onClick={() => (showClosingTextSelection = true)} />
				<IconButton
					disabled={!closingTextObject}
					iconName="eraser"
					onClick={() => (closingTextObject = undefined)}
				/>
			</div>

			<!--Payment condition-->
			<InputLabel label={$_('settings.basic.document_labels.lbl_default_payment_condition')} />
			<div class="flex gap-2">
				<div class="grow">
					<DefaultTextInput
						propertyName=""
						disabled={true}
						value={paymentPeriodObject?.defaultName || ''}
					/>
				</div>
				<IconButton iconName="search" onClick={() => (showPaymentPeriodSelection = true)} />
				<IconButton
					disabled={!paymentPeriodObject}
					iconName="eraser"
					onClick={() => (paymentPeriodObject = undefined)}
				/>
			</div>
		</div>
	{/snippet}
	{#snippet footer()}
		<div>
			<!-- Buttons-->
			<div class="flex justify-end gap-4">
				<DefaultButton
					secondary={true}
					text={$_('general.lbl_cancel')}
					onClick={() => {
						onModalClose();
					}}
				/>
				<DefaultButton onClick={handlePatch} text={$_('general.lbl_accept')} />
			</div>
		</div>
	{/snippet}
</Modal>

{#if showNameGeneratorSelection}
	<Modal
		onModalClose={() => {
			showNameGeneratorSelection = false;
		}}
	>
		{#snippet header()}
			<ModalHeader text={$_('settings.basic.document_labels.lbl_connect_name_generator_header')} />
		{/snippet}
		{#snippet content()}
			<div class="h-[70vh]">
				<SettingDocumentNameGenerators
					showDetailOnClick={false}
					onSelectionChanged={(item) => {
						if (targetDocumentType != null) {
							//Set the selected type
							nameGeneratorObject = item;
							//close the modal
							showNameGeneratorSelection = false;
						}
					}}
				/>
			</div>
		{/snippet}
	</Modal>
{/if}

{#if showOpeningTextSelection || showClosingTextSelection}
	<Modal
		onModalClose={() => {
			showOpeningTextSelection = false;
			showClosingTextSelection = false;
		}}
	>
		{#snippet header()}
			<ModalHeader text={$_('settings.basic.document_labels.lbl_connect_opening_text_header')} />
		{/snippet}
		{#snippet content()}
			<div class="h-[70vh]">
				<TextBlocks
					{textBlockStore}
					onSelectionChanged={(textBlock: TextBlock) => {
						//Set the object
						if (showOpeningTextSelection) {
							openingTextObject = textBlock;
						} else {
							closingTextObject = textBlock;
						}

						//close the modal
						showOpeningTextSelection = false;
						showClosingTextSelection = false;
					}}
				/>
			</div>
		{/snippet}
	</Modal>
{/if}

{#if showPaymentPeriodSelection}
	<Modal
		onModalClose={() => {
			showPaymentPeriodSelection = false;
		}}
	>
		{#snippet header()}
			<ModalHeader
				text={$_('settings.basic.document_labels.lbl_connect_payment_condition_header')}
			/>
		{/snippet}
		{#snippet content()}
			<div class="h-[70vh]">
				<SettingPaymentPeriods
					showDetailOnClick={false}
					onSelectionChanged={(item) => {
						//Set the object
						paymentPeriodObject = item;
						//Close the modal
						showPaymentPeriodSelection = false;
					}}
				/>
			</div>
		{/snippet}
	</Modal>
{/if}
